/**
 * CoachHomeWidget — Home screen summary for coach_pro tier.
 * Shows active client count, recent client activity, and quick-nav to coaching page.
 */

import { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { listMyClientLinks, type TrainerClientLink } from '@/lib/trainers';
import { Users, ChevronRight } from 'lucide-react-native';

interface CoachSummary {
  activeClients: number;
  recentActivity: number; // workouts in last 7 days across clients
}

export default function CoachHomeWidget() {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const router = useRouter();
  const c = theme.colors;
  const [summary, setSummary] = useState<CoachSummary | null>(null);

  const load = useCallback(async () => {
    if (!profile?.id) return;
    try {
      const links = await listMyClientLinks();
      const activeLinks = links.filter((l: TrainerClientLink) => l.status === 'active');
      const activeClientIds = activeLinks.map((l: TrainerClientLink) => l.client_profile_id);

      let recentActivity = 0;
      if (activeClientIds.length > 0) {
        const d7 = new Date();
        d7.setDate(d7.getDate() - 7);
        try {
          const { count } = await supabase
            .from('workouts')
            .select('id', { count: 'exact', head: true })
            .in('user_id', activeClientIds)
            .gte('performed_at', d7.toISOString());
          recentActivity = count ?? 0;
        } catch {
          // RLS may block
        }
      }

      setSummary({ activeClients: activeClientIds.length, recentActivity });
    } catch {
      setSummary(null);
    }
  }, [profile?.id]);

  useEffect(() => { load(); }, [load]);

  if (!summary) return null;

  return (
    <TouchableOpacity
      style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}
      activeOpacity={0.7}
      onPress={() => router.push('/coach-dashboard' as any)}
    >
      <View style={styles.header}>
        <Users size={18} color={c.accent} />
        <Text style={[styles.title, { color: c.text }]}>COACHING</Text>
        <View style={{ flex: 1 }} />
        <ChevronRight size={18} color={c.textTertiary} />
      </View>
      <View style={styles.statsRow}>
        <View style={styles.stat}>
          <Text style={[styles.statValue, { color: c.text }]}>{summary.activeClients}</Text>
          <Text style={[styles.statLabel, { color: c.textSecondary }]}>Active Clients</Text>
        </View>
        <View style={[styles.divider, { backgroundColor: c.border }]} />
        <View style={styles.stat}>
          <Text style={[styles.statValue, { color: c.text }]}>{summary.recentActivity}</Text>
          <Text style={[styles.statLabel, { color: c.textSecondary }]}>Client Workouts (7d)</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    gap: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stat: {
    flex: 1,
    alignItems: 'center',
    gap: 2,
  },
  statValue: {
    fontSize: 24,
    fontWeight: '900',
  },
  statLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  divider: {
    width: 1,
    height: 36,
    marginHorizontal: 12,
  },
});
